import { FunctionComponent, PropsWithChildren } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import {
  Bronze,
  Gold,
  Silver,
} from '@sorare/core/src/components/club/Trophies';
import { glossary } from '@sorare/core/src/lib/glossary';
import { WithRewardRange } from '@sorare/core/src/lib/rewards';
import { unitMapping } from '@sorare/core/src/lib/style';

const StyledBodyS = styled(BodyS)`
  line-height: 1;
`;

const TROPHY_ICONS: {
  [key: number]: FunctionComponent<PropsWithChildren>;
} = {
  1: Gold,
  2: Silver,
  3: Bronze,
};

type Props = WithRewardRange<{
  gap?: keyof typeof unitMapping;
}>;

export const RankRewardTitle = ({
  gap = 1,
  startRank,
  endRank,
  startPct,
  endPct,
}: Props) => {
  const start = startRank ?? (startPct || 0);
  const end = endRank ?? (endPct || 0);

  const TrophyComponent = TROPHY_ICONS[start];

  return (
    <>
      {start === end ? (
        <Horizontal>
          {TrophyComponent && <TrophyComponent />}
          <StyledBodyS as="p">
            <Ellipsis>
              <FormattedMessage
                id="PrizePoolLeaderboard.RankRewards.ordinalPlace"
                defaultMessage="{ordinal} place"
                values={{
                  ordinal: <Ordinal ordinal={start} i18n={glossary.ordinal} />,
                }}
              />
            </Ellipsis>
          </StyledBodyS>
        </Horizontal>
      ) : (
        <Horizontal gap={gap}>
          <StyledBodyS as="p">
            {startRank ? (
              <Ordinal ordinal={start} i18n={glossary.ordinal} />
            ) : (
              <FormattedNumber value={start} style="percent" />
            )}
          </StyledBodyS>
          <StyledBodyS as="p">-</StyledBodyS>
          <StyledBodyS as="p">
            {endRank ? (
              <Ordinal ordinal={end} i18n={glossary.ordinal} />
            ) : (
              <FormattedNumber value={end} style="percent" />
            )}
          </StyledBodyS>
        </Horizontal>
      )}
    </>
  );
};
